import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { logError } from '~/common/utils/log';
import type { LocationRestrictionModalOverrides } from '~/domains/protectionGuards/types';
import { GeoComplyReasonCode } from '~/services/interceptors/set-location.interceptor';
import GeoComplySingleton from '~/utils/geocomply';

import { MarketQueryKeys } from '../constants/query';
import { getMarketAccount } from '../services/account.service.api';
import type { MarketAccountResponse } from '../services/interfaces/account.types';

import { getMarketAccountQueryKey } from './useMarketAccountQueryKey';
import { useMarketAccountSettings } from './useMarketAccountSettings';

const PREDICTION_MARKETS_DISALLOWED_TAG = 'prediction_markets';

export const isMarketLocationAllowed = (account?: MarketAccountResponse) =>
  !account?.state_config?.disallowed?.includes(PREDICTION_MARKETS_DISALLOWED_TAG);

export const formatMarketLocation = (state?: string) => state?.toUpperCase() ?? '';

export const useMarketLocationAllowed = () => {
  const accountQuery = useMarketAccountSettings();
  const queryClient = useQueryClient();

  const refreshAccount = useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: getMarketAccountQueryKey(GeoComplySingleton.getLocationTokenData()?.state),
        queryFn: () => getMarketAccount(),
        staleTime: 0,
      }),
    [queryClient]
  );

  const recheckLocation = useCallback(async () => {
    try {
      await GeoComplySingleton.getValidLocationToken(0, true, {
        reasonCode: GeoComplyReasonCode.LOGIN,
      });

      return isMarketLocationAllowed(await refreshAccount());
    } catch (error) {
      logError('Markets location recheck failed', error);

      return false;
    }
  }, [refreshAccount]);

  const state = accountQuery.data?.state_config?.state;

  const locationRestrictionModal = useMemo<LocationRestrictionModalOverrides>(
    () => ({
      variant: 'predictionMarkets',
      detectedLocation: formatMarketLocation(state),
      queryKey: MarketQueryKeys.ACCOUNT_ALL(),
      onRecheckLocation: recheckLocation,
    }),
    [state, recheckLocation]
  );

  return {
    isAllowed: isMarketLocationAllowed(accountQuery.data),
    state,
    isLoading: accountQuery.isLoading,
    locationRestrictionModal,
    refreshAccount,
  };
};
